// External libraries
import React from 'react';
import ReactDOM from 'react-dom';
import { StoreProvider } from 'easy-peasy';
import { ApolloProvider } from '@apollo/react-hooks';
import { BrowserRouter } from 'react-router-dom';

// Internal modules
import { appStore } from '@/store';
import { apolloClient } from '@/lib/apollo';
import { App } from './components/App';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  browserFeatureDetection,
  logPerformanceTimingOnLoadEvent,
} from '@/lib/browser';
import {
  features,
  getCheckoutSession,
  getCheckoutSessionFlags,
  getGlobals,
  getShippingMethodsVariant,
  isLiteEligible,
  isProd,
  redirectToXoon,
} from '@/lib';
import { isFunction } from '@/lib/utils/typeUtils';
import { configureLoggerForFPTI } from '@/lib/logger/fpti';

import {
  IS_ADDRESSLESS_PAYLOAD,
  logger,
  PAGE_LOAD_PAYLOAD,
  PREPARE_BILLING_PAYLOAD,
} from '@/lib/logger';
import { isIncontext } from '@/lib/utils/incontext';

// load the healthCheck so the function is available on the dom when onload event fires
import '@/lib/health-check';

import { DevApp } from '@/dev';
import { setupAtomics } from './lib/atomics';

const {
  isInternalRequest,
  user: { ecToken: token },
  isBasl,
} = getGlobals();

// Initializations
const isOptionalSignup = () =>
  features.someActive('isOptionalSignup', 'isPOMAEligible');
const hasPrefilledShippingAddress = () =>
  features.isActive('hasPrefilledShippingAddress');
const hasPrefilledShippingOptions = () =>
  features.isActive('hasPrefilledShippingOptions');
const isSntSupported = () => features.isActive('isSntSupported');

// configures logger.fpti() with common payload so some of
// these attrs get added to all FPTI payloads
configureLoggerForFPTI(logger, {
  isWps: features.isActive('isWps'),
  mode: isOptionalSignup() ? 'guest' : 'signup',
  shipping_address_collection: hasPrefilledShippingAddress()
    ? 'merchant_prefill'
    : 'none',
  shipping_preference: hasPrefilledShippingOptions()
    ? 'merchant_prefill'
    : 'none',
  token,
  variant: getShippingMethodsVariant(isSntSupported()),
});

const landingPage = isBasl ? 'BASL' : 'LOGIN';
logger.fpti(`LANDING_PAGE_${landingPage}`);

const isBillingAgreement = () => features.isActive('isBillingAgreement');
if (isBillingAgreement()) {
  const billingType = features.get('billingType') || 'UNKNOWN';
  const checkoutSessionType =
    getGlobals()?.initialGqlData?.checkoutSession?.checkoutSessionType || '';
  logger.fpti(`LANDING_PAGE_${checkoutSessionType}_${billingType}`, {
    state_name: 'checkout_session_type',
    transition_name: `prepare_${checkoutSessionType.toLowerCase()}_${billingType.toLowerCase()}`,
  });
}

const { country, merchantId, name } = getCheckoutSession().merchant ?? {};
const { isWPS } = getCheckoutSessionFlags();
const integrationMethod = isIncontext() ? 'INCONTEXT' : 'FULLPAGE';
const integrationType = isWPS ? 'WPS' : 'EC';

// It adds metadata to (events/tracking/metrics) when the logs are flushed
// This metadata will be used to convert CI/CPL into FPTI events in LNW
logger.addMetadata({
  integrationData: {
    contextId: token,
    contextType: `${integrationType}-TOKEN`,
    integrationMethod,
    integrationType,
  },
  merchantData: {
    merchantCountry: country,
    merchantId,
    merchantName: name,
  },
});

logger.metric('GUEST_SIGNUP.FLOW_INIT');

logPerformanceTimingOnLoadEvent();

// Push the internal redirect route to browser history
// so that app.tsx can load the right route
try {
  const landingPagePath = '/checkoutweb/landing';
  const { route } = window.__INITIAL_DATA__;

  logger.info('serverside_route_details', { route });

  // server route is landing page &&
  // server received an internal redirect request
  // NOTE: this is only done for landing page to start
  // with and this can be expanded to all routes if needed
  if (
    route?.path?.includes?.(landingPagePath) &&
    route?.requestType === 'REDIRECT'
  ) {
    window.history.pushState({}, '', route.path);
    logger.info('landing_iwc_history_push_success');
  }
} catch (err) {
  logger.error('landing_iwc_history_push_error', {
    error: err.message,
    stack: err.stack,
  });
} finally {
  // TODO: we can remove this after VIP page ramp is stable
  // trigger flush to understand the total count of tokens
  // missing the history.pushState call
  logger.flush();
}

/**
 * @TECH_DEBT @FIX_ME
 * This is a temporary hack to enable ramping in Japan. We need `unorm` for
 * legacy browsers in Japan so we can normalize strings but it is a rather
 * large poylfill that we don't want to load unless necessary. Long term we
 * should go back to using browser specific bundles but this is going to
 * require a bit of debugging to fix issues we were having when we first
 * implemented that.
 *
 * NOTE: Android Browser doesn't support String#normalize at all even though
 * it supports almost all modern features. We may need to keep this and
 * conditionally load it since Android Browser is served the modern bundle.
 * We can also check with analytics to see what kind of traffic we're getting
 * from that browser. It is listed at 0% global traffic on CanIUse.
 * https://caniuse.com/mdn-javascript_builtins_string_normalize
 */
if (!isFunction(String.prototype.normalize)) {
  logger.info('polyfilling_string_prototype_normalize');
  const { ui: uiActions } = appStore.getActions();

  // Toggle the page loading so that the user doesn't interact with
  // the form until the polyfill is available. The GQL data calls will
  // still be kicked off in the background.
  uiActions.toggleIsPageLoading(true);
  import('unorm')
    .then(() => {
      const normalize = typeof String.prototype.normalize;
      logger.info('unorm_polyfill_successfully_loaded', { normalize });
      uiActions.toggleIsPageLoading(false);
    })
    .catch(err => {
      logger.warn('unorm_polyfill_fetch_failed', err);
      redirectToXoon({ reason: 'unorm_polyfill_fetch_failed' });
    });
}

const RenderApp = process.env.NODE_ENV !== 'development' ? App : DevApp;

setupAtomics();

(() => {
  // Client-side check for flow eligibility
  if (!isLiteEligible()) {
    return redirectToXoon({ reason: 'lite_flow_ineligible' });
  }

  ReactDOM.render(
    <ErrorBoundary>
      <ApolloProvider client={apolloClient}>
        <StoreProvider store={appStore}>
          <BrowserRouter>
            <RenderApp />
          </BrowserRouter>
        </StoreProvider>
      </ApolloProvider>
    </ErrorBoundary>,
    document.getElementById('root'),
  );

  const { hasPayLaterIntent } = getCheckoutSessionFlags();
  const isAddressless = features.isActive('isAddressless');
  const isOptionalSignup = features.someActive(
    'isOptionalSignup',
    'isPOMAEligible',
  );
  const hasBank = features.someActive('hasAddBank', 'hasConsumerAddBank');
  const hasPayLaterTab = features.get('bnplExperience') === 'TAB';
  const isTabView = hasBank || hasPayLaterTab;

  logger.fpti('page_interactive', PAGE_LOAD_PAYLOAD(), true);
  logger.fpti(
    'prepare_billing_page',
    PREPARE_BILLING_PAYLOAD({
      hasPayLaterIntent,
      isOptionalSignup,
      isTabView,
    }),
  );
  logger.fpti('is_addressless', IS_ADDRESSLESS_PAYLOAD(isAddressless));

  if (isProd && !isInternalRequest) {
    // Feature detection for Payment Request API
    browserFeatureDetection.paymentRequestApi();

    // Remove initial data object from dom and window if we are in prod
    // and the request isn't internal traffic
    delete window.__INITIAL_DATA__;
    const dataScriptEl = document.getElementById('init-data-script');
    if (dataScriptEl) {
      dataScriptEl.parentElement.removeChild(dataScriptEl);
    }
  }
})();
